import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";
import { saveUserInfoValues } from "@/utils/userInfoData";
import { InputField } from "@/components/common/input";
import { NoteIcon } from "@/components/common/icons/NoteIcon";
import { IAddressAutocompleteFields } from "@/components/common/addressInput/common";
import { AddressAutocompleteInput } from "@/components/common/addressInput";
import { SelectField } from "@/components/common/select";
import { Button } from "@/components/common/button";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import { availableStateOptions } from "@/pages/quiz/base/questions/selectState";

const NUMBER_OF_ANSWERS = 4;

const DEFAULT_ERROR_MESSAGE = "Required";

type FormFieldName = "address" | "address2" | "city" | "zipCode" | "state";

type AddressFieldsValues = Record<FormFieldName, string>;

const CheckoutAddressFields = ({
  question,
  questionValue,
  setQuestionValue,
  setQuestionAnswer,
}: QuizQuestionProps<AddressFieldsValues>) => {
  const [error, setError] = useState<Record<string, string | boolean>>({});

  useEffect(() => {
    saveUserInfoValues(questionValue);
  }, [questionValue]);

  const isAllAnswered =
    Object.values(questionValue).filter(Boolean).length === NUMBER_OF_ANSWERS;

  const isNoErrors = Object.values(error).every(
    (errorMessage) => !errorMessage
  );

  const isFormAnswered = isAllAnswered && isNoErrors;

  const onValueChange = (name: string, value: string) => {
    setQuestionValue({ ...questionValue, [name]: value });

    if (error[name]) {
      validateField(name, value);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;

    onValueChange(name, value);
  };

  const setFieldError = (field: string, errorMessage: string | boolean) => {
    setError((errors) => ({ ...errors, [field]: errorMessage }));
  };

  const validateField = (name: string, value: string) => {
    if (name === "address2") return setFieldError(name, "");

    if (!value) {
      setFieldError(name, DEFAULT_ERROR_MESSAGE);
      return;
    }

    // no special validation for first and second names

    setFieldError(name, "");
  };

  const handleBlur = (e: SyntheticEvent<HTMLInputElement>) => {
    const name = (e.target as HTMLInputElement).name;

    validateField(name, questionValue[name as FormFieldName]);
  };

  const registerField = (name: FormFieldName) => ({
    value: questionValue[name],
    onChange: handleInputChange,
    error: error[name],
    onBlur: handleBlur,
    name,
  });

  const onSubmit = () => {
    if (isFormAnswered) {
      setQuestionAnswer(questionValue);
    }
  };

  const handleAddressInput = async (
    _: string,
    addressData: IAddressAutocompleteFields
  ) => {
    const nweAddressFields: Partial<AddressFieldsValues> = {};
    Object.entries(addressData).forEach(async ([key, value]) => {
      if (["city", "state", "zipCode", "address", "address2"].includes(key)) {
        nweAddressFields[key as FormFieldName] = value;
      }
    });

    setQuestionValue({ ...questionValue, ...nweAddressFields });
  };

  return (
    <div className=" flex flex-col-reverse items-center	 w-full justify-center gap-4 desktop:p-8">
      <div className=" flex flex-col-reverse items-center	 w-full justify-center gap-4 desktop:p-8">
        <div className="text-center max-w-[100%] w-[480px] flex-col flex gap-2">
          <h4
            color="red"
            className="text-center uppercase section-title xs:text-2xl text-xl"
          >
            Shipping Address
          </h4>
          <div>
            <p className="section-title !text-[17px]">
              <NoteIcon />
              If approved we need your address to ship your medication.
            </p>
          </div>
          <AddressAutocompleteInput
            addressValue={questionValue["address"]}
            onChange={handleAddressInput}
          />

          <InputField
            {...registerField("address2")}
            label="Address line 2 - optional"
          />

          <div className="flex gap-3">
            <InputField {...registerField("city")} label="City" />
            <InputField {...registerField("zipCode")} label="Zip Code" />
            <SelectField
              {...registerField("state")}
              onChange={handleInputChange as any}
              onBlur={handleBlur as any}
              options={availableStateOptions}
              label="State"
            />
          </div>

          <Button disabled={!isFormAnswered} onClick={onSubmit} />
        </div>
      </div>
    </div>
  );
};

export { CheckoutAddressFields };
